'use client';

import { useRef, useCallback } from 'react';

interface UseInfiniteScrollOptions {
  /** Whether there are more items to load */
  hasNextPage: boolean;
  /** Whether currently fetching */
  isFetching: boolean;
  /** Callback to fetch next page */
  fetchNextPage: () => void;
  /** Root margin for intersection observer (default: '100px') */
  rootMargin?: string;
}

/**
 * Hook for implementing infinite scroll with IntersectionObserver.
 * Returns a callback ref to attach to a trigger element at the bottom of your list.
 *
 * Uses a callback ref pattern to ensure the observer is set up when the DOM
 * element is actually attached, not on initial render when the ref is null.
 */
export function useInfiniteScroll({
  hasNextPage,
  isFetching,
  fetchNextPage,
  rootMargin = '100px',
}: UseInfiniteScrollOptions) {
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Store reactive values in ref to avoid recreating observer
  const stateRef = useRef({ hasNextPage, isFetching, fetchNextPage });
  stateRef.current = { hasNextPage, isFetching, fetchNextPage };

  // Callback ref - called when element is attached/detached from DOM
  const fetchTriggerRef = useCallback(
    (node: HTMLElement | null) => {
      // Clean up previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      // If no node, element was unmounted - nothing more to do
      if (!node) return;

      // Create and attach new observer
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          const { hasNextPage, isFetching, fetchNextPage } = stateRef.current;
          if (entry.isIntersecting && hasNextPage && !isFetching) {
            fetchNextPage();
          }
        },
        { rootMargin, threshold: 0 }
      );

      observerRef.current.observe(node);
    },
    [rootMargin]
  );

  return { fetchTriggerRef };
}
